import React, { useMemo, useCallback } from 'react';

import './Roles.scss';
import { Button } from 'primereact/button';
import { ServerResponsiveTable } from '../../components/ServerResponsiveTable/ServerResponsiveTable';
import { TableColumn, TableAction } from '../../types/responsiveTable';
import { PrimeIcons } from 'primereact/api';
import { Role, RolesTableEntry } from '../../types/role';
import { useClient } from '../../hooks/useClient';
import { useOrganizationContext } from '../../context/OrganizationContext';
import useSWR, { mutate } from 'swr';
import { RolePanel } from '../../components/RolePanel/RolePanel';
import { useResourcePanel } from '../../hooks/useResourcePanel';
import { showDeleteDialog } from '../../utils/dialogUtils';
import { hasFeaturesPermission } from '../../utils/featureUtils';
import { ALL_FEATURES } from '../../config/features';
import { useAuthContext } from '../../context/AuthContext';
import { ExportButton } from '../../components/ExportButton/ExportButton';

export const getRoleDeleteConfirmationDialogProps = (role: Role | undefined) => {
  return {
    message: (
      <>
        ¿Estás seguro de que quieres borrar el rol <b>{role?.name}</b>?
      </>
    ),
    header: 'Eliminar rol',
  };
};

const columns: TableColumn<RolesTableEntry>[] = [
  {
    label: 'Nombre',
    accessor: 'name',
    type: 'string',
    isTitle: true,
  },
  {
    label: 'Usuarios usando',
    accessor: 'usersCount',
    type: 'number',
  },
  {
    label: 'Fecha de creación',
    accessor: 'createdAt',
    type: 'calendar',
  },
];

export const Roles = () => {
  const { get, delete: del } = useClient();
  const { organization } = useOrganizationContext();
  const { features, featuresLoading } = useAuthContext();

  const {
    isEditing,
    setIsEditing,
    panelVisible,
    closePanel,
    selectedResource,
    openPanel: originalOpenPanel,
    isDeleting,
    setIsDeleting,
  } = useResourcePanel<RolesTableEntry>(undefined);

  const openPanel = useCallback(
    async (isEditing: boolean, resourceOrId?: RolesTableEntry | string) => {
      if (typeof resourceOrId === 'string') {
        const role = await get<RolesTableEntry>(`/roles/${resourceOrId}`);
        originalOpenPanel(isEditing, role);
      } else {
        originalOpenPanel(isEditing, resourceOrId);
      }
    },
    [get, originalOpenPanel],
  );

  const handleDelete = useCallback(
    async (id: string | undefined) => {
      if (!id || !organization?.id) return false;
      setIsDeleting(true);
      const response = await del<Role>(`/roles/${id}`, {
        errorMessages: {
          summary: 'No se pudo eliminar el rol',
          defaultDetail: 'Hubo un error al intentar borrar el rol',
          RESOURCE_IN_USE: 'El rol está siendo usado',
        },
        successMessage: {
          summary: 'Rol eliminado',
          detail: 'El rol se ha eliminado correctamente',
        },
      });

      if (response) {
        // The table reads `.../roles-table/paginated?page=…` (useServerTable):
        // revalidate it, whatever page, sort or filter it shows (#73).
        const tableKey = `/organizations/${organization.id}/roles-table/paginated?`;
        mutate((key) => typeof key === 'string' && key.startsWith(tableKey));
      }
      setIsDeleting(false);
      return !!response;
    },
    [del, organization, setIsDeleting],
  );

  const userHasEditFeature = hasFeaturesPermission([ALL_FEATURES.ROLES], { features, featuresLoading });

  const options: TableAction[] = useMemo(
    () => [
      {
        label: 'Ver',
        onClick: (id) => {
          openPanel(false, id);
        },
        hasPermission: userHasEditFeature,
      },
      {
        label: 'Editar',
        onClick: (id) => {
          openPanel(true, id);
        },
        hasPermission: userHasEditFeature,
      },
      {
        label: 'Eliminar',
        onClick: async (id) => {
          const role = selectedResource?.id === id ? selectedResource : await get<RolesTableEntry>(`/roles/${id}`);
          showDeleteDialog(() => handleDelete(id), getRoleDeleteConfirmationDialogProps(role));
        },
        hasPermission: userHasEditFeature,
      },
    ],
    [userHasEditFeature, openPanel, handleDelete, selectedResource, get],
  );

  const renderHeaderOptions = useCallback(
    (fetchAllForExport: () => Promise<RolesTableEntry[]>) => (
      <div className='roles-header-options'>
        <Button
          label='Añadir rol'
          outlined
          rounded
          icon={PrimeIcons.PLUS}
          onClick={() => {
            openPanel(false);
          }}
        />
        <ExportButton fetchData={fetchAllForExport} columns={columns} fileName='Roles' disabled={isDeleting} />
      </div>
    ),
    [openPanel, isDeleting],
  );

  return (
    <>
      <ServerResponsiveTable
        tableId='roles'
        endpoint={organization ? `/organizations/${organization.id}/roles-table/paginated` : ''}
        columns={columns}
        options={options}
        headerOptions={renderHeaderOptions}
        onRowClick={(row) => {
          openPanel(false, row.id);
        }}
        disabled={isDeleting}
      />
      <RolePanel
        resource={selectedResource}
        visible={panelVisible}
        onHide={closePanel}
        onIsEditingChange={setIsEditing}
        onDeleteResource={handleDelete}
        isDeleting={isDeleting}
        isEditing={isEditing}
      />
    </>
  );
};
